import { createContext, createElement, FunctionComponent } from "preact";
import { PropsWithChildren } from "preact/compat";
import { useContext, useEffect, useReducer } from "preact/hooks";
import { Services } from "@/services/services";
import { Suspense } from "@/services/suspense";

const SuspenseContext = createContext<Suspense>(null!);

export const SuspenseProvider = (props: PropsWithChildren<{ services: Services }>) => {
    return createElement(SuspenseContext.Provider, { value: props.services.suspense }, props.children);
}

const useSuspenseContext = (key: string) => {
    const suspense = useContext(SuspenseContext);

    suspense.ensure(key);

    // useEffect(() => {
    //     return () => {
    //         suspense.drop(key);
    //     };
    // }, [suspense, key]);

    return suspense.get(key);
};

const duplicates = new Map<string, any>();

export const useSuspenseLoader = function <Type>(params: { key: string, loader: () => Promise<Type> }): Type {
    const [val, update] = useReducer<number, void>((val) => {
        console.log(`update ${params.key} ${val} -> ${val + 1}`);
        return val + 1;
    }, 0);

    if (!duplicates.has(params.key)) {
        duplicates.set(params.key, update);
        console.log(`useSuspenseLoader: registered key: ${params.key}`);
    }

    if (duplicates.get(params.key) !== update) {
        console.error(`useSuspenseLoader: duplicate key detected: ${params.key}`);
    }

    const state = useSuspenseContext(params.key);

    useEffect(() => {
        console.log(`mounted ${params.key}`);
        return () => {
            console.log(`unmounteed ${params.key}`);
        };
    }, [params.key]);

    console.log(`useSuspenseLoader ${params.key}`, val);

    if (state.step === 'blank') {
        const awaiter = params.loader().then(
            item => {
                console.log(`resolved ${params.key}`);

                state.step = 'done';
                state.awaiter = undefined;
                state.target = item;

                update();
            },
            error => {
                console.log(`catched ${params.key}`);

                state.step = 'done';
                state.awaiter = undefined;
                state.target = undefined;

                console.error(error);
            }
        );

        state.step = 'processing';
        state.awaiter = awaiter;
        state.target = undefined;
    }

    if (state.step === 'processing') {
        console.log(`awaiter ${params.key}`);
        throw state.awaiter;
    }

    return state.target;
};

export const pageSuspense = function <T extends object>(component: FunctionComponent) {
    const ComponentWrapper = (props: T) => {
        return createElement(component, props);
    };

    ComponentWrapper._forwarded = true;

    return ComponentWrapper;
};